import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Avatar,
  Popper,
  Grow,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Alert,
  Grid,
  Link,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import EventIcon from '@mui/icons-material/Event';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CategoryIcon from '@mui/icons-material/Category';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import PresentToAllIcon from '@mui/icons-material/PresentToAll';
import StorefrontIcon from '@mui/icons-material/Storefront';
import GroupIcon from '@mui/icons-material/Group';
import UpdateIcon from '@mui/icons-material/Update';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import ApiService from '../../../../services/api';
import logger from '../../../../utils/logger';
import { formatDateTime } from '../../../../utils/dateUtils';
import { useAdminData } from '../../../../contexts/AdminDataContext';

const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(3),
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

const TitleBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const TitleIcon = styled(Box)(({ theme }) => ({
  width: '56px',
  height: '56px',
  borderRadius: '16px',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  boxShadow: '0 8px 24px rgba(59, 130, 246, 0.35)',
}));

const StatusChip = styled(Chip)(({ status }) => ({
  backgroundColor: status === 'Completed' ? '#dcfce7' : '#fef9c3',
  color: status === 'Completed' ? '#15803d' : '#92400e',
  fontWeight: 600,
  borderRadius: '12px',
}));

// Synced with backend/config/pricing.js - Keep ₹1 for testing (basic annually)
const planPricing = {
  basic: {
    monthly: 1199,
    annually: 1, // ₹1 for testing
  },
  advance: {
    monthly: 1999,
    annually: 19999,
  },
  pro: {
    monthly: 2999,
    annually: 29999,
  },
};

// Addon pricing (synced with backend/config/pricing.js)
const ADDON_NUMBER_TO_GMB = 5000; // ₹5000 one-time fee

const normalizePlanId = (value) => {
  if (!value) return null;
  const cleaned = value.toString().toLowerCase().trim();
  if (cleaned.startsWith('adv')) return 'advance';
  if (cleaned.startsWith('pro')) return 'pro';
  if (cleaned.startsWith('basic')) return 'basic';
  return cleaned;
};

const resolvePrice = (plan, paymentType) => {
  const normalizedPlan = normalizePlanId(plan);
  if (!normalizedPlan || normalizedPlan === 'none') return null;
  const pricing = planPricing[normalizedPlan];
  if (!pricing) return null;
  const normalizedType = paymentType === 'one_shot' ? 'annually' : 'monthly';
  return pricing[normalizedType];
};

const formatDateLiteral = (date) => {
  const candidate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(candidate.getTime())) return null;
  return candidate.toISOString().split('T')[0];
};

const computePlanDates = (planName, paymentType, paymentDate) => {
  const normalizedPlan = normalizePlanId(planName);
  if (!normalizedPlan || normalizedPlan === 'none') {
    return { plan_start_date: null, plan_end_date: null };
  }
  if (!paymentType) return { plan_start_date: null, plan_end_date: null };
  const lowerType = paymentType.toString().toLowerCase();
  const hasPlanInterval = lowerType === 'one_shot' || lowerType === 'emi' || lowerType === 'ecs';
  if (!hasPlanInterval) return { plan_start_date: null, plan_end_date: null };
  const start = new Date(paymentDate || formatDateLiteral(new Date()));
  if (Number.isNaN(start.getTime())) {
    return { plan_start_date: null, plan_end_date: null };
  }
  const end = new Date(start);
  if (lowerType === 'one_shot') {
    end.setFullYear(end.getFullYear() + 1);
  } else {
    end.setDate(end.getDate() + 30);
  }
  return {
    plan_start_date: formatDateLiteral(start),
    plan_end_date: formatDateLiteral(end),
  };
};

const InfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  background: '#fff',
  height: '100%',
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  borderBottom: '2px solid #f0f0f0',
}));

const CardIconBox = styled(Box)(({ bgcolor }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  background: bgcolor || 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  padding: theme.spacing(1.5, 0),
  borderBottom: '1px solid #f5f5f5',
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const DetailIcon = styled(Box)(({ theme }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  backgroundColor: '#f0f9ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(1.5),
  color: '#3b82f6',
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  color: '#6b7280',
  fontWeight: 500,
  minWidth: '120px',
}));

const DetailValue = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#1f2937',
  fontWeight: 500,
  flex: 1,
}));

const ActivityItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: '12px',
  backgroundColor: '#f8fafc',
  marginBottom: theme.spacing(1.5),
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f0fdf4',
    transform: 'translateX(4px)',
  },
}));

const ActivityAvatar = styled(Avatar)(({ theme }) => ({
  width: '36px',
  height: '36px',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  fontSize: '14px',
  fontWeight: 600,
}));

const BackButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 500,
  padding: '8px 16px',
}));

const MeetingProfile = () => {
  const navigate = useNavigate();
  const { meetingId: routeMeetingId, id: legacyId } = useParams();
  const location = useLocation();
  const { actions: adminActions } = useAdminData();

  const meetingId = useMemo(() => {
    let found = routeMeetingId || legacyId;
    if (!found && location?.search) {
      const qsId = new URLSearchParams(location.search).get('id');
      if (qsId) found = qsId;
    }
    if (!found && location?.pathname) {
      const parts = location.pathname.split('/').filter(Boolean);
      const last = parts[parts.length - 1];
      if (last && /\d+/.test(last)) found = last;
    }
    return found;
  }, [routeMeetingId, legacyId, location]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [meeting, setMeeting] = useState(null);
  const [payment_verified, setPaymentVerified] = useState(false);
  const [bdms, setBdms] = useState([]);
  const [tellyTeam, setTellyTeam] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [gmbSnackOpen, setGmbSnackOpen] = useState(false);
  const gmbAnchorRef = useRef(null);
  const [formState, setFormState] = useState({
    status: '',
    meetingWith: '',
    teleMeeting: false,
    presentationShown: false,
    haveGoogleMyBusiness: false,
    comment: '',
    reschedule_date: '',
    reschedule_time: '',
  });
  const [gmbQuery, setGmbQuery] = useState('');
  const [gmbResults, setGmbResults] = useState([]);
  const [gmbLoading, setGmbLoading] = useState(false);
  const [selectedGmb, setSelectedGmb] = useState(null);
  const [closeMeetingOpen, setCloseMeetingOpen] = useState(false);
  const [closeFormData, setCloseFormData] = useState({
    business_name: '',
    category: '',
    contact_person: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    alt_number: '',
    whatsapp: '',
    website: '',
    gst_number: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'India',
    plan: 'basic',
    comment: '',
    tags: '',
    welcome_call: false,
    bdm_name: '',
    bdm_employee_id: '', // BDM employee assignment
    lead_source: '',
    gmb_account_id: '',
    // Services checkboxes
    is_gmb_services: true,
    is_smo_services: false,
    is_website_service: false,
    is_other_services: false,
    // Telly team assignment
    telly_employee_id: '',
    // Payment details
    payment_amount: 1,
    payment_date: new Date().toISOString().split('T')[0],
    payment_status: 'pending',
    payment_method: '',
    payment_reference: '',
    payment_type: 'one_shot', // 'emi' (monthly) or 'one_shot' (yearly)
    // Generated payment link
    generated_payment_link: '',
    is_manual_payment: false,
    // Addon: Add Number to GMB Profile (₹5000 one-time)
    addNumberToGMB: false,
  });

  const fetchMeeting = useCallback(async () => {
    if (!meetingId) {
      setError('Missing meeting id in the route.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await ApiService.get(`/admin/meeting/get-meeting/${meetingId}`);
      const data = res?.data?.data || res?.data || res;
      setMeeting(data);
      setFormState({
        status: data?.status || 'Scheduled',
        meetingWith: data?.meeting_with || '',
        teleMeeting: data?.telly_meeting === '1' || data?.telly_meeting === 1 || data?.telly_meeting === true,
        presentationShown: data?.presentation_show === '1' || data?.presentation_show === 1 || data?.presentation_show === true,
        haveGoogleMyBusiness: !!data?.gmb_status,
        comment: data?.comment || '',
        reschedule_date: data?.meeting_date ? new Date(data.meeting_date).toISOString().split('T')[0] : '',
        reschedule_time: data?.meeting_date ? new Date(data.meeting_date).toTimeString().slice(0, 5) : '',
      });
    } catch (e) {
      logger.error('Failed to load meeting:', e);
      setError(e?.response?.data?.message || e?.message || 'Failed to load meeting details.');
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  const fetchBdms = useCallback(async () => {
    try {
      const res = await ApiService.get('/admin/employee/get-bdm');
      const root = res?.data ?? res;
      const candidates = [root?.data?.bdm, root?.bdm, root?.data, Array.isArray(root) ? root : null];
      const found = candidates.find(a => Array.isArray(a));
      const list = Array.isArray(found) ? found : [];
      const normalized = list.map((u) => ({
        id: u.id,
        name: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.full_name || u.name || 'BDM',
      }));
      setBdms(normalized);
    } catch (e) {
      logger.warn('Failed to load BDM list:', e);
      setBdms([]);
    }
  }, []);

  const fetchTellyTeam = useCallback(async () => {
    try {
      // Use dedicated endpoint for Telly team
      const res = await ApiService.get('/admin/employee/get-telly');
      const data = res?.data?.data || res?.data || res;
      const employees = data?.telly || data?.employees || [];

      const tellyMembers = employees.map(e => ({
        id: e.id,
        name: [e.first_name, e.last_name].filter(Boolean).join(' ') || e.full_name || 'Telly Agent'
      }));
      setTellyTeam(tellyMembers);
    } catch (e) {
      logger.warn('Failed to load Telly team:', e);
      setTellyTeam([]);
    }
  }, []);

  useEffect(() => {
    fetchMeeting();
    fetchBdms();
    fetchTellyTeam();
  }, [fetchMeeting, fetchBdms, fetchTellyTeam]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('close') === 'true' && meeting && !meeting.status) {
      setCloseMeetingOpen(true);
    }
  }, [location.search, meeting]);

  const resetCloseForm = useCallback(() => {
    if (meeting) {
      const lead = meeting.leadDetails || {};
      const emp = meeting.employeeDetails || {};
      setCloseFormData({
        business_name: lead.business_name || (meeting.clientDetails?.full_name || ''),
        category: lead.category || '',
        contact_person: lead.contact_person || (meeting.clientDetails?.full_name || ''),
        first_name: lead.first_name || '',
        last_name: lead.last_name || '',
        email: lead.email || lead.contact_email || '',
        phone_number: lead.phone_number || '',
        alt_number: lead.alt_number || '',
        whatsapp: lead.whatsapp || lead.phone_number || '',
        website: lead.website || '',
        gst_number: lead.gst_number || '',
        address_line1: lead.address_line1 || '',
        address_line2: lead.address_line2 || '',
        city: lead.city || '',
        state: lead.state || '',
        zip_code: lead.zip_code || '',
        country: lead.country || 'India',
        plan: lead.package || 'basic',
        comment: meeting.comment || lead.comment || '',
        tags: lead.tags || '',
        welcome_call: !!lead.welcome_call,
        bdm_name: [emp.first_name, emp.last_name].filter(Boolean).join(' ') || emp.full_name || '',
        bdm_employee_id: lead.bdm_employee_id || meeting.bdm_employee_id || meeting.employee_id || '',
        lead_source: lead.lead_source || '',
        gmb_account_id: lead.gmb_account_id || '',
        is_gmb_present: true,
        is_gmb_services: lead.is_gmb_services ?? true,
        is_smo_services: lead.is_smo_services ?? false,
        is_website_service: lead.is_website_service ?? false,
        is_other_services: lead.is_other_services ?? false,
        payment_type: 'one_shot',
        payment_amount: 1,
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: '',
        payment_reference: '',
        generated_payment_link: '',
        is_manual_payment: false,
      });
      setSelectedGmb(null);
      setGmbQuery('');
      setPaymentVerified(false);
    }
  }, [meeting]);

  // Only reset the close form when FIRST loading a meeting (not on every meeting update)
  const [initialFormLoaded, setInitialFormLoaded] = useState(false);
  useEffect(() => {
    if (meeting && !initialFormLoaded) {
      resetCloseForm();
      setInitialFormLoaded(true);
    }
  }, [meeting, initialFormLoaded, resetCloseForm]);

  // Reset the flag when navigating to a different meeting
  useEffect(() => {
    setInitialFormLoaded(false);
  }, [meetingId]);

  const derived = useMemo(() => {
    const d = meeting || {};
    const lead = d.leadDetails || {};
    const emp = d.employeeDetails || {};
    const client = d.clientDetails || d.userDetails || {};
    const createdBy = d.createdby?.full_name || '';
    const name = lead.business_name || client.full_name || 'Meeting';
    const statusLabel =
      (d.status === 'completed' || d.status === 'COMPLETED' || d.status === 'closed_won' || d.status === true || d.status === '1')
        ? 'COMPLETED'
        : (d.status === 'rescheduled' || d.status === 'RESCHEDULE') ? 'RESCHEDULE'
          : (d.status === 'not_available' || d.status === 'NOT AVAILABLE') ? 'NOT AVAILABLE'
            : (d.status === 'not_interested' || d.status === 'NOT INTERESTED') ? 'NOT INTERESTED'
              : (d.status || 'Scheduled');
    const leadType = lead.type || '-';
    const services = [
      lead.is_gmb_services ? 'GMB' : null,
      lead.is_smo_services ? 'SMO' : null,
      lead.is_website_service ? 'Website' : null,
    ].filter(Boolean);
    const address = [lead.address_line1, lead.city, lead.state, lead.zip_code, lead.country].filter(Boolean).join(', ');
    const scheduledOn = (() => {
      const dt = d.date_time || d.meeting_date;
      if (!dt) return '-';
      return formatDateTime(dt);
    })();
    return {
      name,
      statusLabel,
      leadType,
      client: lead.business_name || '-',
      ownerName: emp.first_name || '-',
      ownerEmail: emp.official_email || '-',
      services,
      contactPerson: lead.contact_person || '-',
      contactNumber: lead.phone_number || lead.alt_number || '-',
      address: address || '-',
      comment: d.comment || lead.comment || '-',
      scheduledOn,
      assigned: createdBy || '-',
    };
  }, [meeting]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (name === 'haveGoogleMyBusiness' && type === 'checkbox' && checked) {
      setGmbSnackOpen(true);
    }
  };

  useEffect(() => {
    if (!gmbSnackOpen) return undefined;
    const t = setTimeout(() => setGmbSnackOpen(false), 3000);
    return () => clearTimeout(t);
  }, [gmbSnackOpen]);

  const handleUpdate = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        status: formState.status,
        meeting_with: formState.meetingWith !== '' && formState.meetingWith != null ? Number(formState.meetingWith) : undefined,
        telly_meeting: !!formState.teleMeeting,
        presentation_show: !!formState.presentationShown,
        gmb_status: !!formState.haveGoogleMyBusiness,
        comment: formState.comment || undefined,
        meeting_date: formState.status === 'RESCHEDULE' ? formState.reschedule_date : undefined,
        date_time: formState.status === 'RESCHEDULE' ? `${formState.reschedule_date} ${formState.reschedule_time || '10:00'}` : undefined,
      };

      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

      await ApiService.put(`/admin/meeting/update-meeting/${meetingId}`, payload);
      setSnackbarOpen(true);

      // Redirect to listing after short delay to show success
      setTimeout(() => {
        navigate('/admin-dashboard/meetings');
      }, 1500);
    } catch (err) {
      logger.error('Error updating meeting:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update meeting.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseMeetingSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      // Get GMB info for linking (Manual entry prioritized if provided and no selection made)
      // Get GMB info for linking (Manual entry prioritized if provided and no selection made)
      // If selected item is a 'user' type, do NOT send its ID as a GMB location ID (backend will link User via email)
      const isUserType = selectedGmb?.type === 'user' || (selectedGmb?.id && String(selectedGmb.id).startsWith('user-'));
      const gmbLocationId = isUserType ? null : (selectedGmb?.id || selectedGmb?.raw?.location_id || selectedGmb?.location_id || closeFormData.gmb_account_id || null);
      const gmbAccountId = selectedGmb?.raw?.account_id || selectedGmb?.raw?.id || null;
      const leadId = meeting?.leadDetails?.id || meeting?.lead_id;
      const { plan_start_date, plan_end_date } = computePlanDates(closeFormData.plan, closeFormData.payment_type, closeFormData.payment_date);

      const clientDetails = {
        business_name: closeFormData.business_name,
        category: closeFormData.category,
        contact_person: closeFormData.contact_person,
        first_name: closeFormData.first_name,
        last_name: closeFormData.last_name,
        email: closeFormData.email,
        contact_email: closeFormData.email,
        phone_number: closeFormData.phone_number,
        alt_number: closeFormData.alt_number,
        whatsapp: closeFormData.whatsapp,
        website: closeFormData.website,
        gst_number: closeFormData.gst_number,
        address_line1: closeFormData.address_line1,
        address_line2: closeFormData.address_line2,
        city: closeFormData.city,
        state: closeFormData.state,
        zip_code: closeFormData.zip_code,
        country: closeFormData.country,
        package: closeFormData.plan,
        tags: closeFormData.tags,
        welcome_call: closeFormData.welcome_call,
        cro_name: closeFormData.cro_name,
        bdm_name: closeFormData.bdm_name,
        lead_source: closeFormData.lead_source,
        is_gmb_services: closeFormData.is_gmb_services,
        is_smo_services: closeFormData.is_smo_services,
        is_website_service: closeFormData.is_website_service,
        is_other_services: closeFormData.is_other_services,
        payment_amount: closeFormData.payment_amount,
        payment_date: closeFormData.payment_date,
        payment_status: payment_verified ? 'paid' : (closeFormData.is_manual_payment ? 'paid' : closeFormData.payment_status),
        payment_method: closeFormData.is_manual_payment ? closeFormData.payment_method : 'razorpay',
        payment_reference: closeFormData.is_manual_payment ? closeFormData.payment_reference : (closeFormData.generated_payment_link || null),
        payment_type: closeFormData.payment_type,
        telly_employee_id: closeFormData.telly_employee_id || null,
        bdm_employee_id: closeFormData.bdm_employee_id || null,
        bdm_employee_id: closeFormData.bdm_employee_id || null,
        plan_start_date,
        plan_end_date,
        gmb_account_id: gmbLocationId,
      };

      // 1. Call the close-deal endpoint which handles everything
      const closeDealPayload = {
        meeting_id: meetingId,
        lead_id: leadId,
        telly_employee_id: closeFormData.telly_employee_id || null,
        bdm_employee_id: closeFormData.bdm_employee_id || null,
        package_name: closeFormData.plan,
        comments: closeFormData.comment,
        generated_payment_link: closeFormData.generated_payment_link || null,
        ...clientDetails,
      };

      // Call the close-deal endpoint which handles everything (meeting status, lead conversion, etc.)
      const closeDealRes = await ApiService.post('/admin/meeting/close-deal', closeDealPayload);
      const closeDealResult = closeDealRes?.data;
      logger.log('Close deal response:', closeDealResult);

      // 3. If GMB was selected, try to link it to the user's account
      if (gmbLocationId && meeting?.leadDetails?.user_id) {
        try {
          await ApiService.put(`/admin/gmb-account/update/${gmbAccountId || gmbLocationId}`, {
            user_id: meeting.leadDetails.user_id,
            is_verified: true,
            business_name: closeFormData.business_name,
            phone_number: closeFormData.phone_number,
            address: closeFormData.address_line1,
            city: closeFormData.city,
            state: closeFormData.state,
            zip_code: closeFormData.zip_code,
            country: closeFormData.country,
            website: closeFormData.website,
            email: closeFormData.email,
            category: closeFormData.category,
            plan: closeFormData.plan,
          });
          logger.log('GMB account linked successfully');
        } catch (gmbErr) {
          logger.warn('Failed to link GMB account:', gmbErr);
        }
      }

      // Note: GMB connection is handled by asking client to login and using the search panel
      // Payment link is generated manually via the "Generate Razorpay Link" button in the dialog

      // Refresh meeting data and client cache
      await fetchMeeting();
      // Force refresh the clients cache so the new client appears
      if (adminActions?.fetchClients) {
        adminActions.fetchClients(true);
      }
      setSnackbarOpen(true);
      setCloseMeetingOpen(false);

      // Redirect to clients page (since this is now a client) after short delay
      // Redirect to newly created client profile immediately
      setTimeout(() => {
        if (closeDealResult && closeDealResult.user_id) {
          navigate(`/admin-dashboard/clients/${closeDealResult.user_id}`);
        } else {
          // Fallback if no user_id returned (should not happen with new backend update)
          navigate('/admin-dashboard/clients');
        }
      }, 500); // Shorter delay since we want to be "direct"
    } catch (err) {
      logger.error('Error closing meeting:', err);
      setError(err.response?.data?.message || err.message || 'Failed to close meeting.');
      alert('Error closing meeting: ' + (err.response?.data?.message || err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCloseFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  useEffect(() => {
    setCloseFormData(prev => {
      if (!prev.plan || prev.plan === 'none' || !prev.payment_type) {
        return prev.payment_amount === '' ? prev : { ...prev, payment_amount: '' };
      }
      const amount = resolvePrice(prev.plan, prev.payment_type);
      if (!amount) {
        return prev.payment_amount === '' ? prev : { ...prev, payment_amount: '' };
      }
      const amountStr = String(amount);
      return prev.payment_amount === amountStr ? prev : { ...prev, payment_amount: amountStr };
    });
  }, [closeFormData.plan, closeFormData.payment_type]);

  // Auto-fill form when GMB is selected
  const handleGmbSelect = (gmbOption) => {
    setSelectedGmb(gmbOption);
    if (!gmbOption?.raw) return;
    const gmb = gmbOption.raw;
    // Parse location_data if it's a string
    let locationData = {};
    if (gmb.location_data) {
      try {
        locationData = typeof gmb.location_data === 'string'
          ? JSON.parse(gmb.location_data)
          : gmb.location_data;
      } catch (e) {
        locationData = {};
      }
    }

    // Extract address components
    const address = locationData?.storefrontAddress || gmb.address_data || {};
    const addressLines = address?.addressLines || [];

    // Improved name/email extraction based on result type
    const isGmb = gmbOption.type === 'gmb';
    const isUser = gmbOption.type === 'user';
    const isLead = gmbOption.type === 'lead';

    const ownerName = (
      (isGmb ? (gmb.user?.full_name || gmb.business_name || gmb.location_name) : null) ||
      (isUser ? (gmb.user?.full_name || gmb.full_name) : null) ||
      (isLead ? (gmb.contact_person || gmb.business_name) : null) ||
      gmbOption.label || ''
    ).replace(/^\[.*?\]\s*/, '').trim();

    const ownerParts = ownerName.split(/\s+/).filter(Boolean);
    const derivedEmail = gmb.email || (isGmb ? gmb.user?.email : null) || (isUser ? gmb.user?.email : null) || '';
    const derivedTags = gmb.category || (isLead ? 'lead' : '');
    const gmbLocId = isGmb ? (gmb.location_id || gmb.id) : (isLead ? gmb.gmb_account_id : '');

    // Update form with GMB/User/Lead data
    setCloseFormData(prev => {
      const displayName = ownerName || prev.contact_person;
      return {
        ...prev,
        business_name: gmb.business_name || (isGmb ? (locationData?.title || locationData?.name) : null) || prev.business_name,
        category: gmb.category || (isGmb ? locationData?.primaryCategory?.displayName : null) || prev.category,
        phone_number: gmb.phone_number || (isGmb ? locationData?.phoneNumbers?.primaryPhone : null) || prev.phone_number,
        website: gmb.website || (isGmb ? locationData?.websiteUri : null) || prev.website,
        address_line1: addressLines[0] || gmb.address_line1 || address?.formattedAddress || prev.address_line1,
        address_line2: addressLines[1] || gmb.address_line2 || prev.address_line2,
        city: address?.locality || gmb.city || prev.city,
        state: address?.administrativeArea || gmb.state || prev.state,
        zip_code: address?.postalCode || gmb.zip_code || prev.zip_code,
        country: address?.regionCode || gmb.country || prev.country || 'India',
        contact_person: displayName || prev.contact_person,
        first_name: ownerParts[0] || prev.first_name,
        last_name: ownerParts.slice(1).join(' ') || prev.last_name,
        email: derivedEmail || prev.email,
        alt_number: prev.alt_number || gmb.phone_number || prev.alt_number,
        whatsapp: prev.whatsapp || gmb.phone_number || prev.whatsapp,
        plan: gmb.plan || prev.plan,
        lead_source: prev.lead_source || (isLead ? 'Lead Source' : (isGmb ? 'Google My Business' : 'Existing User')),
        tags: derivedTags || prev.tags,
        gmb_account_id: gmbLocId || prev.gmb_account_id,
        // Mark GMB services as selected if a GMB or Lead is selected
        is_gmb_services: isGmb || isLead || prev.is_gmb_services,
      };
    });

    // Show success message
    logger.log('GMB profile selected:', gmb.business_name || gmbOption.label);
  };

  const searchGmbByEmail = async (query) => {
    const q = (query || '').trim();
    // if (!q) return setGmbResults([]); // Allow empty query to fetch recent signups
    try {
      setGmbLoading(true);
      // Unified search: searches USERS first (email, name, phone), then gets their GMB profiles
      const res = await ApiService.get(`/admin/gmb-account/search?q=${encodeURIComponent(q)}&limit=25`);
      const data = res?.data?.data || res?.data || res;
      const accounts = data?.accounts || [];

      setGmbResults(accounts.map(p => {
        const type = p._type || 'gmb';
        const businessName = p.business_name || p.location_name || (type === 'user' ? (p.full_name || p.user?.full_name) : 'Unknown Business');
        const userEmail = p.user?.email || p.email || '';
        const userName = p.user?.full_name || p.user?.first_name || p.contact_person || '';

        // Enhance label with type tag for clarity
        let prefix = '';
        if (q === '' && type === 'gmb') prefix = '[Recent] ';
        else if (type === 'user') prefix = '[User] ';
        else if (type === 'lead') prefix = '[Lead] ';
        else prefix = '[GMB] ';

        const label = userEmail
          ? `${prefix}${businessName} (${userEmail})`
          : userName
            ? `${prefix}${businessName} (${userName})`
            : `${prefix}${businessName}`;

        return {
          id: p.location_id || p.id,
          label,
          type,
          raw: p
        };
      }));
    } catch (e) {
      setGmbResults([]);
    } finally {
      setGmbLoading(false);
    }
  };

  useEffect(() => {
    const q = (gmbQuery || '').trim();
    const t = setTimeout(() => {
      // If query is present, search. If empty, it will fetch recent 5 from our updated backend
      searchGmbByEmail(q);
    }, q ? 250 : 0); // Instant for empty (recent), debounced for typing
    return () => clearTimeout(t);
  }, [gmbQuery]);

  const getInitials = (name) => {
    if (!name || name === '-') return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading && !meeting) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Typography variant="h6" color="text.secondary">Loading meeting details...</Typography>
        </Box>
      </Container>
    );
  }

  if (error && !meeting) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Typography variant="h6" color="error">{error}</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <HeaderSection>
        <TitleBox>
          <TitleIcon>
            <EventIcon sx={{ fontSize: 28 }} />
          </TitleIcon>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
              <Typography variant="h4" fontWeight={700} color="#1f2937">
                {derived.name}
              </Typography>
              <Chip
                label="Meeting"
                size="small"
                sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 600 }}
              />
            </Box>
            <StatusChip label={derived.statusLabel} status={derived.statusLabel} size="small" />
          </Box>
        </TitleBox>
        <BackButton
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          Back
        </BackButton>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/admin-dashboard/meeting/edit/${meetingId}`)}
          sx={{
            bgcolor: '#3b82f6',
            color: 'white',
            borderRadius: '12px',
            padding: '10px 20px',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              bgcolor: '#2563eb',
            }
          }}
        >
          Edit Meeting
        </Button>
      </HeaderSection>

      {error && (
        <Box sx={{ bgcolor: '#fef2f2', color: '#dc2626', p: 2, borderRadius: '10px', mb: 3 }}>
          {error}
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3, mb: 3 }}>
        <InfoCard>
          <CardHeader>
            <CardIconBox bgcolor="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)">
              <BusinessIcon sx={{ fontSize: 20 }} />
            </CardIconBox>
            <Typography variant="h6" fontWeight={600} color="#1f2937">Meeting Information</Typography>
          </CardHeader>
          <DetailRow>
            <DetailIcon><CategoryIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Lead Type</DetailLabel>
            <DetailValue>{derived.leadType}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailIcon><BusinessIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Client</DetailLabel>
            <DetailValue sx={{ color: '#3b82f6', fontWeight: 600 }}>{derived.client}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailIcon><PersonIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Owner Name</DetailLabel>
            <DetailValue>{derived.ownerName}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailIcon><EmailIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Owner Email</DetailLabel>
            <DetailValue>{derived.ownerEmail}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailIcon><StorefrontIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Services</DetailLabel>
            <DetailValue>
              {derived.services.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {derived.services.map((service, idx) => (
                    <Chip key={idx} label={service} size="small" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 500 }} />
                  ))}
                </Box>
              ) : '-'}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailIcon><PersonIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Contact Person</DetailLabel>
            <DetailValue>{derived.contactPerson}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailIcon><PhoneIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Contact Number</DetailLabel>
            <DetailValue>{derived.contactNumber}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailIcon><LocationOnIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Address</DetailLabel>
            <DetailValue>{derived.address}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailIcon><ScheduleIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Scheduled On</DetailLabel>
            <DetailValue>{derived.scheduledOn}</DetailValue>
          </DetailRow>
        </InfoCard>

        <InfoCard>
          <CardHeader>
            <CardIconBox bgcolor="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)">
              <HistoryIcon sx={{ fontSize: 20 }} />
            </CardIconBox>
            <Typography variant="h6" fontWeight={600} color="#1f2937">Activity Timeline</Typography>
          </CardHeader>
          <ActivityItem>
            <ActivityAvatar>{getInitials(derived.assigned)}</ActivityAvatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={500} color="#1f2937">Meeting Scheduled</Typography>
              <Typography variant="body2" color="text.secondary">by <Typography component="span" color="#10b981" fontWeight={500}>{derived.assigned}</Typography></Typography>
              <Typography variant="caption" color="#9ca3af" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <ScheduleIcon sx={{ fontSize: 12 }} /> {derived.scheduledOn}
              </Typography>
            </Box>
          </ActivityItem>
        </InfoCard>
      </Box>

      <InfoCard>
        <CardHeader>
          <CardIconBox bgcolor="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)">
            <UpdateIcon sx={{ fontSize: 20 }} />
          </CardIconBox>
          <Typography variant="h6" fontWeight={600} color="#1f2937">Update Meeting</Typography>
        </CardHeader>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select name="status" value={formState.status} onChange={handleChange} label="Status" sx={{ borderRadius: '10px' }}>
              <MenuItem value="Scheduled">Scheduled</MenuItem>
              <MenuItem value="RESCHEDULE">RESCHEDULE</MenuItem>
              <MenuItem value="NOT AVAILABLE">NOT AVAILABLE</MenuItem>
              <MenuItem value="NOT INTERESTED">NOT INTERESTED</MenuItem>
              <MenuItem value="COMPLETED">COMPLETED</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Reschedule Date/Time Selection */}
        <Collapse in={formState.status === 'RESCHEDULE'}>
          <Box sx={{ p: 2, mb: 3, bgcolor: '#fef3c7', borderRadius: '12px', border: '1px solid #fcd34d' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#92400e', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon sx={{ fontSize: 18 }} /> Select New Meeting Date & Time
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <TextField
                fullWidth
                label="New Date"
                type="date"
                name="reschedule_date"
                value={formState.reschedule_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                sx={{ bgcolor: '#fff', borderRadius: '10px' }}
              />
              <TextField
                fullWidth
                label="New Time"
                type="time"
                name="reschedule_time"
                value={formState.reschedule_time}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                sx={{ bgcolor: '#fff', borderRadius: '10px' }}
              />
            </Box>
          </Box>
        </Collapse>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: '12px' }}>
          <FormControlLabel control={<Checkbox checked={formState.teleMeeting} onChange={handleChange} name="teleMeeting" sx={{ color: '#10b981', '&.Mui-checked': { color: '#10b981' } }} />} label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><VideoCallIcon sx={{ color: '#10b981', fontSize: 20 }} /><Typography>Tele Meeting</Typography></Box>} />
          <FormControlLabel control={<Checkbox checked={formState.presentationShown} onChange={handleChange} name="presentationShown" sx={{ color: '#3b82f6', '&.Mui-checked': { color: '#3b82f6' } }} />} label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PresentToAllIcon sx={{ color: '#3b82f6', fontSize: 20 }} /><Typography>Presentation Shown</Typography></Box>} />
          <Box ref={gmbAnchorRef} sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <FormControlLabel control={<Checkbox checked={formState.haveGoogleMyBusiness} onChange={handleChange} name="haveGoogleMyBusiness" sx={{ color: '#f59e0b', '&.Mui-checked': { color: '#f59e0b' } }} />} label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><StorefrontIcon sx={{ color: '#f59e0b', fontSize: 20 }} /><Typography>Has Google My Business</Typography></Box>} />
            <Popper open={gmbSnackOpen} anchorEl={gmbAnchorRef.current} placement="top" transition modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]} sx={{ zIndex: 1600 }}>
              {({ TransitionProps }) => (
                <Grow {...TransitionProps} style={{ transformOrigin: 'bottom left' }} timeout={220}>
                  <Paper sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, background: 'linear-gradient(90deg,#f59e0b,#f97316)', color: '#fff', px: 1.5, py: 0.6, borderRadius: 1, boxShadow: '0 8px 30px rgba(249,115,22,0.18)' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>Ask client to login</Typography>
                    </Box>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Box>
        </Box>
        <TextField fullWidth label="Comment" name="comment" multiline rows={3} value={formState.comment} onChange={handleChange} sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate(-1)} disabled={loading} sx={{ borderRadius: '10px', textTransform: 'none', px: 4 }}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={loading} sx={{ borderRadius: '10px', textTransform: 'none', px: 4, background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>Update Meeting</Button>
          <Button variant="contained" onClick={() => { setCloseMeetingOpen(true); }} disabled={loading || derived.statusLabel === 'COMPLETED' || (!formState.haveGoogleMyBusiness || formState.status !== 'COMPLETED')} sx={{ borderRadius: '10px', textTransform: 'none', px: 4, background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>Close Meeting</Button>
        </Box>
      </InfoCard>

      <Dialog open={closeMeetingOpen} onClose={() => { if (!loading) { setCloseMeetingOpen(false); } }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle>
          <Typography variant="h5" fontWeight={700}>Close Meeting</Typography>
          <Typography variant="body2" color="text.secondary">Finalize client details</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>

            {/* GMB Profile Association */}
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <StorefrontIcon sx={{ fontSize: 20 }} /> Link Google My Business Profile
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select the client's GMB profile from the most recent registrations below to auto-fill the form.
              </Typography>

              {/* Direct Display Section for Recent Signups */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>
                    ⚡ Most Recent Signups
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    disabled={gmbLoading}
                    onClick={() => searchGmbByEmail('')}
                    startIcon={
                      <UpdateIcon
                        sx={{
                          fontSize: 16,
                          animation: gmbLoading ? 'spin 1.5s linear infinite' : 'none',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                          },
                        }}
                      />
                    }
                    sx={{
                      fontSize: '11px',
                      textTransform: 'none',
                      fontWeight: 700,
                      color: '#3b82f6',
                      p: '2px 8px',
                      borderRadius: '8px',
                      '&:hover': { bgcolor: '#eff6ff' }
                    }}
                  >
                    {gmbLoading ? 'Refreshing...' : 'Refresh List'}
                  </Button>
                </Box>

                <Autocomplete
                  fullWidth
                  options={gmbResults.filter(o => o.type === 'gmb' || o.type === 'user').slice(0, 10)}
                  getOptionLabel={(option) => option.label || 'Unknown'}
                  value={selectedGmb}
                  onChange={(event, newValue) => handleGmbSelect(newValue)}
                  onInputChange={(event, newInputValue) => setGmbQuery(newInputValue)}
                  loading={gmbLoading}
                  noOptionsText="No recent signups found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Profile"
                      placeholder="Select recent signup or type to search..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {gmbLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          bgcolor: '#fff'
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', py: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#f1f5f9', color: '#64748b', fontSize: '14px', fontWeight: 700 }}>
                          {(option.label || '?').replace(/^\[.*?\]\s*/, '').charAt(0)}
                        </Avatar>
                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {option.label.replace(/^\[.*?\]\s*/, '')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {option.raw?.email || option.raw?.user?.email || 'No email'}
                          </Typography>
                        </Box>
                        {option.type === 'user' && (
                          <Chip
                            label="USER"
                            size="small"
                            sx={{ ml: 1, height: 20, fontSize: '0.65rem', bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 700 }}
                          />
                        )}
                        {option.type === 'gmb' && (
                          <Chip
                            label="GMB"
                            size="small"
                            sx={{ ml: 1, height: 20, fontSize: '0.65rem', bgcolor: '#eff6ff', color: '#3b82f6', fontWeight: 700 }}
                          />
                        )}
                      </Box>
                    </li>
                  )}
                />
              </Box>
              {selectedGmb && (
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#dcfce7', borderRadius: '8px', border: '1px solid #86efac' }}>
                  <Typography variant="body2" color="success.dark" fontWeight={600}>
                    ✓ GMB Profile Selected: {selectedGmb.label}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Business Information Section */}
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon sx={{ fontSize: 20 }} /> Business Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField fullWidth label="Business Name" name="business_name" value={closeFormData.business_name} onChange={handleCloseFormChange} size="small" required />
                <TextField fullWidth label="Business Category" name="category" value={closeFormData.category} onChange={handleCloseFormChange} size="small" />
                <TextField fullWidth label="GST Number" name="gst_number" value={closeFormData.gst_number} onChange={handleCloseFormChange} size="small" />
                <TextField fullWidth label="Website" name="website" value={closeFormData.website} onChange={handleCloseFormChange} size="small" />
                <TextField fullWidth label="Tags" name="tags" value={closeFormData.tags} onChange={handleCloseFormChange} size="small" placeholder="Comma separated tags" />
                <TextField fullWidth label="Lead Source" name="lead_source" value={closeFormData.lead_source} onChange={handleCloseFormChange} size="small" />
                <TextField fullWidth label="GMB Location ID" name="gmb_account_id" value={closeFormData.gmb_account_id} onChange={handleCloseFormChange} size="small" placeholder="e.g. locations/123..." />
              </Box>
            </Box>

            {/* Contact Details Section */}
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ fontSize: 20 }} /> Contact Details
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField fullWidth label="Contact Person" name="contact_person" value={closeFormData.contact_person} onChange={handleCloseFormChange} size="small" />
                <TextField fullWidth label="First Name" name="first_name" value={closeFormData.first_name} onChange={handleCloseFormChange} size="small" />
                <TextField fullWidth label="Last Name" name="last_name" value={closeFormData.last_name} onChange={handleCloseFormChange} size="small" />
                <TextField fullWidth label="Email Address" name="email" value={closeFormData.email} onChange={handleCloseFormChange} size="small" type="email" />
                <TextField fullWidth label="Phone Number" name="phone_number" value={closeFormData.phone_number} onChange={handleCloseFormChange} size="small" />
                <TextField fullWidth label="Alt Phone Number" name="alt_number" value={closeFormData.alt_number} onChange={handleCloseFormChange} size="small" />
                <TextField fullWidth label="WhatsApp" name="whatsapp" value={closeFormData.whatsapp} onChange={handleCloseFormChange} size="small" />
              </Box>
            </Box>

            {/* Address Section */}
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOnIcon sx={{ fontSize: 20 }} /> Address
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField fullWidth label="Address Line 1" name="address_line1" value={closeFormData.address_line1} onChange={handleCloseFormChange} size="small" sx={{ gridColumn: '1 / -1' }} />
                <TextField fullWidth label="Address Line 2" name="address_line2" value={closeFormData.address_line2} onChange={handleCloseFormChange} size="small" sx={{ gridColumn: '1 / -1' }} />
                <TextField fullWidth label="City" name="city" value={closeFormData.city} onChange={handleCloseFormChange} size="small" />
                <TextField fullWidth label="State" name="state" value={closeFormData.state} onChange={handleCloseFormChange} size="small" />
                <TextField fullWidth label="ZIP Code" name="zip_code" value={closeFormData.zip_code} onChange={handleCloseFormChange} size="small" />
                <TextField fullWidth label="Country" name="country" value={closeFormData.country} onChange={handleCloseFormChange} size="small" />
              </Box>
            </Box>

            {/* Plan & Payment Section - Combined */}

            <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              💳 Plan & Subscription
            </Typography>
            <Box sx={{ p: 2, bgcolor: '#f0f9ff', borderRadius: '12px', border: '1px solid #93c5fd' }}>
              {/* Selected GMB Profile */}
              {selectedGmb ? (
                <Box sx={{ p: 1.5, bgcolor: '#dcfce7', borderRadius: '8px', border: '1px solid #86efac', mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} color="success.dark">
                    ✓ GMB Profile: {selectedGmb.label || selectedGmb.raw?.business_name}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: 1.5, bgcolor: '#fef3c7', borderRadius: '8px', border: '1px solid #fcd34d', mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} color="warning.dark">
                    ⚠️ Please select a GMB profile above first
                  </Typography>
                </Box>
              )}

              {/* Plan & Subscription Section - Compact */}
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: '#475569', letterSpacing: '0.05em' }}>SUBSCRIPTION & PAYMENT</Typography>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Select Plan</InputLabel>
                      <Select
                        name="plan"
                        value={closeFormData.plan}
                        onChange={(e) => {
                          const plan = e.target.value;
                          const prices = {
                            basic: { emi: 1199, one_shot: 1 },
                            advance: { emi: 1999, one_shot: 19999 },
                            pro: { emi: 2999, one_shot: 29999 }
                          };
                          setCloseFormData(prev => ({
                            ...prev,
                            plan,
                            payment_amount: prices[plan]?.[prev.payment_type] || ''
                          }));
                        }}
                        label="Select Plan"
                        sx={{ bgcolor: '#fff' }}
                      >
                        <MenuItem value="basic">Basic</MenuItem>
                        <MenuItem value="advance">Advance</MenuItem>
                        <MenuItem value="pro">Pro</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Billing</InputLabel>
                      <Select
                        name="payment_type"
                        value={closeFormData.payment_type}
                        onChange={(e) => {
                          const type = e.target.value;
                          const prices = {
                            basic: { emi: 1199, one_shot: 1 },
                            advance: { emi: 1999, one_shot: 19999 },
                            pro: { emi: 2999, one_shot: 29999 }
                          };
                          setCloseFormData(prev => ({
                            ...prev,
                            payment_type: type,
                            payment_amount: prices[prev.plan]?.[type] || ''
                          }));
                        }}
                        label="Billing"
                        sx={{ bgcolor: '#fff' }}
                      >
                        <MenuItem value="emi">Monthly (EMI)</MenuItem>
                        <MenuItem value="one_shot">Yearly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Total"
                      value={`₹${(Number(closeFormData.payment_amount) + (closeFormData.addNumberToGMB ? ADDON_NUMBER_TO_GMB : 0)).toLocaleString()}`}
                      InputProps={{ readOnly: true }}
                      sx={{ bgcolor: '#f0fdf4', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#bbf7d0' }, '& .MuiInputBase-input': { fontWeight: 700, color: '#15803d' } }}
                    />
                  </Grid>
                </Grid>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={closeFormData.addNumberToGMB}
                      onChange={(e) => setCloseFormData(prev => ({ ...prev, addNumberToGMB: e.target.checked }))}
                      size="small"
                      sx={{ color: '#ea580c', '&.Mui-checked': { color: '#ea580c' } }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: '#334155' }}>
                      Add Number to GMB Profile (<b>+₹5,000</b> one-time)
                    </Typography>
                  }
                  sx={{ mb: 2, ml: 0.5 }}
                />

                <Divider sx={{ my: 2, borderColor: '#e2e8f0' }} />

                {/* Payment Generation / Manual Toggle */}
                {!closeFormData.is_manual_payment ? (
                  <Box>
                    {closeFormData.generated_payment_link ? (
                      <Box sx={{ p: 2, bgcolor: '#ecfdf5', borderRadius: '12px', border: '1px solid #6ee7b7' }}>
                        <Typography variant="subtitle2" fontWeight={700} color="#059669" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon fontSize="small" /> Subscription Link Ready
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            value={closeFormData.generated_payment_link}
                            InputProps={{
                              readOnly: true,
                              style: { fontSize: '0.85rem', color: '#4b5563', backgroundColor: '#fff', borderRadius: '8px' }
                            }}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d1fae5' },
                              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10b981' }
                            }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                          <Button
                            variant="containted"
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(closeFormData.generated_payment_link);
                              // Could add a toast here
                              alert('Link copied to clipboard!');
                            }}
                            startIcon={<ContentCopyIcon fontSize="small" />}
                            sx={{
                              flex: 1,
                              bgcolor: '#fff',
                              color: '#059669',
                              border: '1px solid #d1fae5',
                              fontWeight: 600,
                              textTransform: 'none',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                              '&:hover': { bgcolor: '#f0fdf4', borderColor: '#34d399' }
                            }}
                          >
                            Copy Link
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={async () => {
                              if (navigator.share) {
                                try {
                                  await navigator.share({
                                    title: 'Complete your Subscription',
                                    text: `Please use this link to complete your subscription for ${closeFormData.business_name || 'your plan'}.`,
                                    url: closeFormData.generated_payment_link
                                  });
                                } catch (err) {
                                  console.log('Error sharing:', err);
                                }
                              } else {
                                navigator.clipboard.writeText(closeFormData.generated_payment_link);
                                alert('Link copied (Sharing not supported on this device)');
                              }
                            }}
                            startIcon={<ShareIcon fontSize="small" />}
                            sx={{
                              flex: 1,
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: 'white',
                              fontWeight: 600,
                              textTransform: 'none',
                              boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.4)',
                              '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }
                            }}
                          >
                            Share
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={!closeFormData.plan || closeFormData.plan === 'none' || !selectedGmb || loading}
                        onClick={async () => {
                          if (!selectedGmb) {
                            alert('Please select a GMB profile first');
                            return;
                          }
                          setLoading(true);
                          try {
                            const gmbLocationId = selectedGmb?.id || selectedGmb?.raw?.location_id || selectedGmb?.location_id;
                            const gmbAccountId = selectedGmb?.raw?.account_id || selectedGmb?.raw?.id;

                            // Determine billing cycle
                            const billingCycle = (closeFormData.payment_type === 'emi') ? 'monthly' : 'annually';

                            // Create subscription
                            const response = await ApiService.post('/customer/subscription/create', {
                              planId: closeFormData.plan === 'advance' ? 'advanced' : closeFormData.plan,
                              billingCycle: billingCycle,
                              userEmail: closeFormData.email || '',
                              userPhone: closeFormData.phone_number || '',
                              ...(meeting?.leadDetails?.user_id && !isNaN(Number(meeting.leadDetails.user_id)) ? { userId: Number(meeting.leadDetails.user_id) } : {}),
                              profileKey: gmbLocationId,
                              profileName: closeFormData.business_name || selectedGmb?.label,
                              accountId: gmbAccountId,
                              locationId: gmbLocationId,
                              addonAmount: closeFormData.addNumberToGMB ? ADDON_NUMBER_TO_GMB : 0,
                              addon_selected: closeFormData.addNumberToGMB,
                              notes: {
                                source: 'admin_close_meeting',
                                meeting_id: meetingId || meeting?.id,
                                lead_id: meeting?.leadDetails?.id || meeting?.lead_id,
                                business_name: closeFormData.business_name,
                                addNumberToGMB: closeFormData.addNumberToGMB
                              }
                            });

                            const link = response?.data?.shortUrl || response?.data?.data?.shortUrl || response?.shortUrl;
                            if (link) {
                              setCloseFormData(prev => ({ ...prev, generated_payment_link: link, payment_status: 'pending' }));
                            } else {
                              alert('Failed to generate subscription link. Please try again.');
                            }
                          } catch (err) {
                            console.error('Error generating subscription:', err);
                            alert(err.response?.data?.message || 'Failed to generate subscription link');
                          } finally {
                            setLoading(false);
                          }
                        }}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                        sx={{
                          py: 1.5,
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                          fontWeight: 700,
                          fontSize: '1rem',
                          textTransform: 'none',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                            boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)',
                          }
                        }}
                      >
                        {loading ? 'Generating Payment Link...' : 'Generate Subscription Link'}
                      </Button>
                    )}
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Link
                        component="button"
                        variant="caption"
                        onClick={() => setCloseFormData(prev => ({ ...prev, is_manual_payment: true }))}
                        sx={{ color: '#64748b', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline', color: '#475569' } }}
                      >
                        Switch to Manual Payment Record
                      </Link>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                      <TextField
                        size="small"
                        label="Payment Date"
                        type="date"
                        name="payment_date"
                        value={closeFormData.payment_date}
                        onChange={handleCloseFormChange}
                        InputLabelProps={{ shrink: true }}
                        sx={{ bgcolor: '#fff' }}
                      />
                      <FormControl size="small" fullWidth>
                        <InputLabel>Method</InputLabel>
                        <Select
                          name="payment_method"
                          value={closeFormData.payment_method}
                          onChange={handleCloseFormChange}
                          label="Method"
                          sx={{ bgcolor: '#fff' }}
                        >
                          <MenuItem value="cash">Cash</MenuItem>
                          <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                          <MenuItem value="cheque">Cheque</MenuItem>
                          <MenuItem value="upi_manual">UPI (Manual)</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        size="small"
                        label="Reference / Receipt #"
                        name="payment_reference"
                        value={closeFormData.payment_reference}
                        onChange={handleCloseFormChange}
                        sx={{ gridColumn: 'span 2', bgcolor: '#fff' }}
                      />
                    </Box>
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Link
                        component="button"
                        variant="caption"
                        onClick={() => setCloseFormData(prev => ({ ...prev, is_manual_payment: false }))}
                        sx={{ color: '#64748b', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline', color: '#475569' } }}
                      >
                        Switch to Online Payment Link
                      </Link>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Notes */}

              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes (Optional)"
                name="comment"
                value={closeFormData.comment}
                onChange={handleCloseFormChange}
                placeholder="Any notes about this deal..."
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => { setCloseMeetingOpen(false); }} disabled={loading}>Cancel</Button>

          {/* If link generated, force them to wait for payment or use manual confirm */}
          {closeFormData.generated_payment_link ? (
            payment_verified ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1, px: 2, bgcolor: '#dcfce7', borderRadius: '8px', border: '1px solid #86efac', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={600} color="success.dark">
                    🎉 Payment Verified! Plan Activated.
                  </Typography>
                </Box>
                <Button
                  onClick={handleCloseMeetingSubmit}
                  variant="contained"
                  disabled={loading}
                  sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', px: 3 }}
                >
                  {loading ? 'Closing...' : 'Close & Save'}
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Waiting for payment capture...
                </Typography>
                <Button
                  onClick={async () => {
                    setLoading(true);
                    try {
                      // 1. Manually trigger a sync from Razorpay (great for testing or slow webhooks)
                      let syncSuccess = false;
                      try {
                        const syncRes = await ApiService.get(`/admin/meeting/sync-payment/${meetingId}`);
                        if (syncRes?.data?.status === 'success' || syncRes?.status === 200) {
                          syncSuccess = true;
                        }
                      } catch (syncErr) {
                        console.warn('Sync attempt warning:', syncErr.response?.data?.message || syncErr.message);
                      }

                      // 2. Fetch updated meeting data
                      const res = await ApiService.get(`/admin/meeting/get-meeting/${meetingId}`);
                      const updatedData = res?.data?.data || res?.data || res;
                      setMeeting(updatedData);

                      // Check actual data integrity or sync success
                      const isPaidInDb = updatedData?.leadDetails?.payment_status === 'paid';

                      if (isPaidInDb || syncSuccess) {
                        setPaymentVerified(true);
                        setCloseFormData(prev => ({ ...prev, payment_status: 'paid' }));
                        // Show success animation/UI instead of alert
                        // alert('Payment verified successfully! You can now click "Close & Save" to finalize the meeting.');
                      } else {
                        alert('Payment not yet detected. If you just paid, wait 10 seconds and try again.');
                      }
                    } catch (e) {
                      alert('Error checking status: ' + (e.response?.data?.message || e.message));
                    } finally {
                      setLoading(false);
                    }
                  }}
                  variant="outlined"
                  color={payment_verified ? "success" : "primary"}
                  size="small"
                  disabled={loading}
                >
                  {payment_verified ? "✅ Payment Verified" : "🔄 Refresh Status"}
                </Button>
              </Box>
            )
          ) : (
            <Button
              onClick={handleCloseMeetingSubmit}
              variant="contained"
              disabled={loading || (closeFormData.is_manual_payment && (!closeFormData.payment_method || !closeFormData.payment_reference)) || (!closeFormData.is_manual_payment && !payment_verified)}
              sx={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}
            >
              {loading ? 'Closing...' : (closeFormData.is_manual_payment ? 'Confirm Payment & Close' : 'Close & Save')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Box sx={{ bgcolor: '#3b82f6', color: 'white', px: 2.5, py: 1.5, borderRadius: '12px', boxShadow: '0 10px 40px rgba(59, 130, 246, 0.3)' }}>
          <Typography sx={{ fontWeight: 600 }}>Action Successful</Typography>
        </Box>
      </Snackbar>
    </Container >
  );
};

export default MeetingProfile;
